import { type Property } from 'csstype';
import React, {
  Children,
  ReactElement,
  type ReactNode,
  cloneElement,
  isValidElement,
  useEffect,
  useRef,
  useState,
} from 'react';
import { HiCheck, HiX } from 'react-icons/hi';

import { BaseTooltip } from '@gbs-monorepo-packages/common';
import { type ITextStatusProps } from '../TextStatus';
import { TableButtonActionsCellContainer } from './styles';

export interface ITableButtonActionsCellProps
  extends Pick<ITextStatusProps, 'dataCy' | 'type'> {
  alignItems?: Property.AlignItems;
  children: ReactNode;
}

export const TableButtonActionsCell = ({
  dataCy = 'table-button-action-cell',
  alignItems,
  children,
}: ITableButtonActionsCellProps): JSX.Element => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isCompact, setIsCompact] = useState(false);

  useEffect(() => {
    const checkWidth = () => {
      if (containerRef.current) {
        setIsCompact(containerRef.current.offsetWidth < 135);
      }
    };

    checkWidth();
    window.addEventListener('resize', checkWidth);

    return () => {
      window.removeEventListener('resize', checkWidth);
    };
  }, []);

  const renderChildren = () => {
    if (!isCompact) {
      return children;
    }
    return Children.map(children, (child) => {
      if (isValidElement(child) && child.props.children) {
        const childText = String(child.props.children);
        const lowerText = childText.toLowerCase();
        if (lowerText === 'approve' || lowerText === 'approved') {
          return (
            <BaseTooltip message={childText}>
              {cloneElement(child as ReactElement, {
                ...child.props,
                children: <HiCheck />,
              })}
            </BaseTooltip>
          );
        }
        if (lowerText === 'decline' || lowerText === 'declined') {
          return (
            <BaseTooltip message={childText}>
              {cloneElement(child as ReactElement, {
                ...child.props,
                children: <HiX />,
              })}
            </BaseTooltip>
          );
        }
      }
      return child;
    });
  };

  return (
    <TableButtonActionsCellContainer
      ref={containerRef}
      data-cy={dataCy}
      $alignItems={alignItems}
      $isCompact={isCompact}
    >
      {renderChildren()}
    </TableButtonActionsCellContainer>
  );
};
